import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable } from 'rxjs';
import { ChangePasswordDto, PasswordEditDto } from 'src/app/account/core/models/passwords.model';
import { CreateOrUpdateUserInput, EntityDtoOfInt64, GetUserForEditOutput, PagedResultDtoOfUserListDto, PasswordComplexityResponseModelData, PasswordComplexitySettings, UserListDto, UserListFilter } from 'src/app/admin/core/models/user.model';
import { GetUserPermissionsForEditOutput, UpdateUserPermissionsInput } from 'src/app/_shared/core/models/permissions.model';
import { AdminHttpService } from './admin-http.service';

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  // public fields
  isUsersLoading$: Observable<boolean>;
  isUsersLoadingSubject: BehaviorSubject<boolean>;
  totalUsersLength$: Observable<number>;
  totalUsersLengthSubject: BehaviorSubject<number>;
  isUserLoading$: Observable<boolean>;
  isUserLoadingSubject: BehaviorSubject<boolean>;

  private _isLoading$ = new BehaviorSubject<boolean>(false);

  get isLoading$() {
    return this._isLoading$.asObservable();
  }

  constructor(
    private adminHttpService: AdminHttpService
  ) {
    this.isUsersLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isUsersLoading$ = this.isUsersLoadingSubject.asObservable();

    this.totalUsersLengthSubject = new BehaviorSubject<number>(0);
    this.totalUsersLength$ = this.totalUsersLengthSubject.asObservable(); 

    this.isUserLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isUserLoading$ = this.isUserLoadingSubject.asObservable();
  }

  getAllUsers(input: UserListFilter): Observable<PagedResultDtoOfUserListDto> {
    this.isUsersLoadingSubject.next(true);
    this._isLoading$.next(true);
    return this.adminHttpService.getAllUsers(input).pipe(
      map((pagedUsers: any) => {
        if (pagedUsers) {
          this.totalUsersLengthSubject.next(pagedUsers.result.totalCount);
        }
        return pagedUsers.result
      }),
      finalize(() => {
        this.isUsersLoadingSubject.next(false);
        this._isLoading$.next(false);
      })
    );
  }

  getAllDropDownUsers(): Observable<UserListDto[]> {
    this.isUsersLoadingSubject.next(true);
    this._isLoading$.next(true);
    return this.adminHttpService.getAllDropDownUsers().pipe(
      map((pagedUsers: any) => {
        return pagedUsers
      }),
      finalize(() => {
        this.isUsersLoadingSubject.next(false);
        this._isLoading$.next(false);
      })
    );
  }
  

  getUserForEdit(id: number): Observable<GetUserForEditOutput> {
    this.isUserLoadingSubject.next(true);
    return this.adminHttpService.getUserForEdit(id).pipe(
      map((user: any) => {
        return user.result
      }),
      finalize(() => this.isUserLoadingSubject.next(false))
    );
  }

  createOrUpdateUser(input: CreateOrUpdateUserInput): Observable<void> {
    if (input.user.id) {
      return this.adminHttpService.updateUser(input).pipe(
        map((res: any) => {
          return res;
        })
      );
    }
    else {
      return this.adminHttpService.createUser(input).pipe(
        map((res: any) => {
          return res;
        })
      );
    }
    
  }

  lockUser(id: EntityDtoOfInt64): Observable<void> {
    return this.adminHttpService.lockUser(id).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  unlockUser(id: EntityDtoOfInt64): Observable<void> {
    return this.adminHttpService.unlockUser(id).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  deleteUser(id: number): Observable<void> {
    return this.adminHttpService.deleteUser(id).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  updatePassword(input: PasswordEditDto): Observable<void> {
    return this.adminHttpService.updatePassword(input).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  changePassword(input: ChangePasswordDto): Observable<void> {
    return this.adminHttpService.changePassword(input).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getUserPermissionsForEdit(id: number): Observable<GetUserPermissionsForEditOutput> {
    return this.adminHttpService.getUserPermissionsForEdit(id).pipe(
      map((user: any) => {
        return user.result
      }),
      finalize(() => this.isUserLoadingSubject.next(false))
    );
  }

  updateUserPermissions(input: UpdateUserPermissionsInput): Observable<void> {
    return this.adminHttpService.updateUserPermissions(input).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  resetUserSpecificPermissions(input: EntityDtoOfInt64): Observable<void> {
    return this.adminHttpService.resetUserSpecificPermissions(input).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  getPasswordComplexitySetting(): Observable<PasswordComplexitySettings> {
    return this.adminHttpService.getPasswordComplexitySetting().pipe(
      map((passwordComplexitySettings: PasswordComplexityResponseModelData) => {
        if (!passwordComplexitySettings?.result?.setting) {
          throw new Error("Failed to retrieve security settings");
        }

        return passwordComplexitySettings.result.setting;
      })
    );
  }

}
